import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Shell = ({ title, updated, children, testid }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid={testid} className="min-h-screen bg-[var(--paper)]">
      <header className="border-b border-[var(--line)] bg-white/70 backdrop-blur sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)] hover:text-[var(--petrol)]" data-testid="legal-back">
            <ArrowLeft size={14} /> Torna al sito
          </Link>
          <span className="font-serif text-lg text-[var(--ink)]">Gabriella Ritondo</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-serif text-4xl sm:text-5xl text-[var(--ink)] mb-3">{title}</h1>
        <p className="text-xs uppercase tracking-[0.15em] text-[var(--ink-soft)] mb-12">Ultimo aggiornamento: {updated}</p>
        <div className="legal-body space-y-10 text-[var(--ink)] leading-relaxed">{children}</div>
      </main>
    </div>
  );
};

const H = ({ children }) => <h2 className="font-serif text-2xl text-[var(--petrol)]">{children}</h2>;

export const PrivacyPolicy = () => (
  <Shell title="Privacy Policy" updated="ottobre 2026" testid="privacy-page">
    <section>
      <H>1. Titolare del trattamento</H>
      {/* TODO: aggiungere P.IVA, sede legale e dati fiscali quando disponibili (struttura già predisposta) */}
      <p>Il titolare del trattamento dei dati personali è <strong>Maria Gabriella Ritondo</strong> — Gabriella Ritondo Holding, contattabile all'indirizzo email <a className="link-underline text-[var(--petrol)]" href="mailto:gabriellaritondo@gmail.com">gabriellaritondo@gmail.com</a>.</p>
    </section>
    <section>
      <H>2. Dati raccolti</H>
      <p>Attraverso questo sito raccogliamo esclusivamente i dati che ci fornisci volontariamente:</p>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Moduli di contatto e prenotazione Executive Call</strong>: nome, email, azienda, ruolo, messaggio, preferenze orarie.</li>
        <li><strong>Candidature Academy</strong>: nome, email, azienda, ruolo e motivazioni.</li>
        <li><strong>Newsletter</strong>: nome ed email.</li>
        <li><strong>Acquisti e membership</strong>: dati di pagamento gestiti interamente da Stripe (noi non vediamo né conserviamo i dati della carta), email per la consegna dei materiali.</li>
        <li><strong>Statistiche di utilizzo</strong>: tracking interno anonimo e, solo previo consenso, Google Analytics 4 con Consent Mode v2 (vedi Cookie Policy).</li>
      </ul>
    </section>
    <section>
      <H>3. Finalità e base giuridica</H>
      <p>I dati sono trattati per: rispondere alle richieste di contatto e prenotazione (art. 6.1.b GDPR); gestire candidature e iscrizioni ai programmi (art. 6.1.b); evadere ordini e consegnare i prodotti digitali (art. 6.1.b); inviare la newsletter, solo con il tuo consenso (art. 6.1.a); statistiche aggregate di utilizzo del sito, solo con consenso per quanto riguarda Google Analytics (art. 6.1.a).</p>
    </section>
    <section>
      <H>4. Conservazione</H>
      <p>I dati di contatto e candidatura sono conservati per il tempo necessario a gestire il rapporto e comunque non oltre 24 mesi dall'ultimo contatto. I dati fiscali legati agli acquisti sono conservati per i termini di legge (10 anni). I dati di analytics sono conservati secondo le impostazioni predefinite di Google Analytics (14 mesi).</p>
    </section>
    <section>
      <H>5. Terze parti che trattano i dati</H>
      <ul className="list-disc pl-6 space-y-2 mt-3">
        <li><strong>Stripe</strong> (pagamenti) — Stripe Payments Europe, Ltd.</li>
        <li><strong>Google Analytics 4</strong> (statistiche, solo con consenso) — Google Ireland Ltd.</li>
        <li><strong>Resend</strong> (invio email transazionali).</li>
        <li><strong>Emergent / provider cloud</strong> (hosting del sito e database).</li>
      </ul>
      <p className="mt-3">Nessun dato viene venduto o ceduto a terzi per finalità di marketing.</p>
    </section>
    <section>
      <H>6. I tuoi diritti</H>
      <p>Ai sensi degli artt. 15-22 GDPR puoi richiedere in qualsiasi momento accesso, rettifica, cancellazione, limitazione, portabilità e opposizione al trattamento, nonché revocare il consenso prestato. Scrivi a <a className="link-underline text-[var(--petrol)]" href="mailto:gabriellaritondo@gmail.com">gabriellaritondo@gmail.com</a>. Hai inoltre diritto di reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it).</p>
    </section>
  </Shell>
);

export const CookiePolicy = () => (
  <Shell title="Cookie Policy" updated="ottobre 2026" testid="cookie-page">
    <section>
      <H>1. Cosa sono i cookie</H>
      <p>I cookie sono piccoli file di testo che i siti visitati salvano sul tuo dispositivo. Questo sito utilizza un numero minimo di cookie e tecnologie analoghe, elencate di seguito.</p>
    </section>
    <section>
      <H>2. Cookie tecnici (sempre attivi)</H>
      <p>Necessari al funzionamento del sito: preferenza di consenso (<code>gr_consent</code>), identificativo anonimo di sessione statistica (<code>gr_cid</code>), cookie di sessione dell'area riservata amministratore. Non richiedono consenso.</p>
    </section>
    <section>
      <H>3. Cookie statistici — Google Analytics 4 (solo con consenso)</H>
      <p>Utilizziamo Google Analytics 4 (ID proprietà 556941244). <strong>Prima del tuo consenso il tracker non viene caricato affatto</strong>: nessun cookie, nessun segnale, nessuna connessione verso Google. Solo se clicchi <strong>"Accetta"</strong> nel banner vengono caricati lo script e i cookie di misurazione (<code>_ga</code>, <code>_ga_*</code>, durata fino a 14 mesi). Se clicchi "Rifiuta", Google Analytics resta completamente disattivato.</p>
      <p className="mt-3">Non utilizziamo cookie pubblicitari né di profilazione: le finalità advertising di Google Analytics sono disattivate lato server.</p>
    </section>
    <section>
      <H>4. Gestione del consenso</H>
      <p>Puoi modificare la tua scelta in qualsiasi momento dal link <strong>"Preferenze cookie"</strong> nel piè di pagina: il banner ti verrà riproposto. Puoi inoltre eliminare i cookie dal tuo browser in qualsiasi momento.</p>
    </section>
    <section>
      <H>5. Terze parti</H>
      <p>Stripe può installare cookie antifrode durante il pagamento (necessari alla sicurezza della transazione). Per maggiori informazioni: Privacy Policy di Stripe e di Google.</p>
    </section>
  </Shell>
);
